import { GameData, Country } from '../types';

const VOWELS = ['A', 'E', 'I', 'O', 'U'];

const getCountryNameLength = (name: string): number => name.replace(/[^a-z]/gi, '').length;

const getSubjectCountry = (subject: string, data: GameData): Country | undefined =>
  data.indexes.byIso3[subject];

// Duplicating validation logic briefly to avoid complex dependency injection in this XML format.
// In a real app, the Registry of Templates would be a singleton.

export const checkAnswer = (
  templateId: string, 
  subject: any, 
  answer: Country, 
  data: GameData
): boolean => {
  // Safe string conversion only if subject is not null/undefined for templates that need it
  const subString = subject ? String(subject) : '';

  if (templateId.startsWith('trivia_')) {
    return !!subString && (data.indexes.byTriviaTag[subString]?.includes(answer.iso3) ?? false);
  }

  switch (templateId) {
    case 'starts_with':
      return !!subString && answer.name.toUpperCase().startsWith(subString);
    case 'ends_with_ia':
      return answer.name.toLowerCase().endsWith('ia');
    case 'starts_with_vowel':
      return VOWELS.includes(answer.name.charAt(0).toUpperCase());
    case 'long_name':
      return getCountryNameLength(answer.name) >= Number(subString);
    case 'exact_length':
    case 'short_name':
      return getCountryNameLength(answer.name) === Number(subString);
    case 'same_first_letter_as': {
      const subjectCountry = getSubjectCountry(subString, data);
      return !!subjectCountry &&
        answer.name.charAt(0).toUpperCase() === subjectCountry.name.charAt(0).toUpperCase();
    }
    case 'in_region':
      return answer.region === subString;
    case 'borders_country':
      // Subject is the ISO3 of the country to border
      // We check if the ANSWER country lists the SUBJECT in its borders
      return !!subString && answer.borders.includes(subString);
    case 'has_no_borders':
      return answer.borders.length === 0;
    case 'many_borders':
      return answer.borders.length >= Number(subString);
    case 'shares_border_with':
      return !!subString && answer.borders.includes(subString);
    case 'uses_currency':
      return !!subString && answer.currencyCodes.includes(subString);
    case 'currency_name_group':
      return !!subString && answer.currencyGroupNames.includes(subString);
    case 'no_own_currency':
      return answer.issuesOwnCurrency === false;
    case 'fractional_unit':
      return !!subString && answer.fractionalUnits.includes(subString);
    case 'no_fractional_unit':
      return answer.fractionalUnitInUse === false;
    case 'capital_is':
      return !!subString && answer.capital === subString;
    case 'capital_starts_with':
      return !!subString && !!answer.capital &&
        answer.capital.toUpperCase().startsWith(subString.toUpperCase());
    case 'flag_identify':
      return !!subString && answer.iso3 === subString;
    default:
      return false;
  }
};
